import React, { useState, useMemo, useEffect } from 'react';
import GenericDrawer from '@components/generic-drawer/GenericDrawer';
import DynamicForm from '@components/_old/dynamicForm/DynamicForm';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { getModelInputs } from './content';
import { supervisorApiBaseUrl } from '@utils/env-utils';
import { getError } from '@utils/graph-utils';
import { DEFAULT_ERROR_TEXT } from '@utils';

const API_URL = `${supervisorApiBaseUrl}/api/Lookups/Insurance-Models`;

interface IModelDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingCodes: string[];
  brandLovs: { [brandCode: string]: { Code: string; Title: string } };
  model?: any;
  isEdit?: boolean;
}

const ModelDrawer: React.FC<IModelDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  existingCodes,
  brandLovs,
  model,
  isEdit = false,
}) => {
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [formValues, setFormValues] = useState<any>(
    model
      ? {
          brand:
            model?.brand || model?.Insurance_Brands || model?.Brand?.Code || '',
          code: model?.code ?? model?.ModelCode ?? '',
          title: model?.title ?? model?.Title ?? '',
        }
      : {
          brand: Object.keys(brandLovs)[0] || '',
          code: '',
          title: '',
        }
  );

  useEffect(() => {
    setFormValues(
      model
        ? {
            brand:
              model?.brand ||
              model?.Insurance_Brands ||
              model?.Brand?.Code ||
              '',
            code: model?.code ?? model?.ModelCode ?? '',
            title: model?.title ?? model?.Title ?? '',
          }
        : {
            brand: Object.keys(brandLovs)[0] || '',
            code: '',
            title: '',
          }
    );
  }, [model, brandLovs, open]);

  const inputsForm = useMemo(
    () => getModelInputs(brandLovs, formValues, isEdit),
    [brandLovs, formValues]
  );

  const handleFormChange = (changed: any, value: any, allValues: any) => {
    setFormValues(allValues);
  };

  const submitForm = async (formValues: Record<string, any>) => {
    setSubmitButtonState('loading');
    setFormDisabled(true);

    let brandCode = '';
    if (isEdit && model) {
      brandCode =
        model.Brand?.Code || model.Insurance_Brands || formValues.brand || '';
    } else {
      brandCode = brandLovs[formValues.brand]?.Code || formValues.brand || '';
    }

    const baseCode = brandCode ? `${brandCode}Model` : '';
    const codePart = formValues.code;
    const fullCode = `${baseCode}${codePart}`;

    const existingFullCode = existingCodes.map((c) => c.toLowerCase());
    const currentModelCode = model?.Code?.toLowerCase() || '';

    const isDuplicate =
      !isEdit || (isEdit && fullCode.toLowerCase() !== currentModelCode)
        ? existingFullCode.includes(fullCode.toLowerCase())
        : false;

    if (isDuplicate) {
      toast.error(<ToastErrorMessage>Model already exists</ToastErrorMessage>);
      setFormDisabled(false);
      setSubmitButtonState(undefined);
      return;
    }

    try {
      let response;
      const payload = {
        values: {
          Insurance_Brands: brandCode,
          Code: fullCode,
          ModelCode: codePart,
          Title: formValues.title,
        },
      };

      if (isEdit && model?.Code) {
        response = await fetch(
          `${API_URL.replace('/Insurance-Models', '')}/Insurance-Models/${model.Code}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }
        );
      } else {
        response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        setSubmitButtonState('success');
        toast.success(
          <ToastSuccessMessage>
            Lookup {isEdit ? 'updated' : 'created'} successfully!
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          setFormDisabled(false);
          setSubmitButtonState(undefined);
          onSuccess();
          onClose();
        }, 500);
      } else {
        let errorMessage = DEFAULT_ERROR_TEXT;
        try {
          const errorJson = await response.json();
          const referenceError = errorJson?.errors?.ReferenceError?.[0];

          if (
            typeof referenceError === 'string' &&
            referenceError.toLowerCase().includes('already exists')
          ) {
            errorMessage = 'Model already exists';
          } else if (typeof referenceError === 'string') {
            errorMessage = referenceError;
          }
        } catch {
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>;
        }

        throw new Error(errorMessage);
      }
    } catch (err: any) {
      setFormDisabled(false);
      setSubmitButtonState(undefined);
      toast.error(
        <ToastErrorMessage>
          {err?.message || DEFAULT_ERROR_TEXT}
        </ToastErrorMessage>
      );
    }
  };

  return (
    <GenericDrawer
      title={isEdit ? 'Edit Model' : 'New Model'}
      isOpen={open}
      onClose={onClose}
    >
      <DynamicForm
        inputs={inputsForm}
        onSubmit={submitForm}
        buttonText={'Submit'}
        submitButtonState={submitButtonState}
        disableForm={formDisabled}
        title=""
        onChange={handleFormChange}
      />
    </GenericDrawer>
  );
};

export default ModelDrawer;
